import { call, all, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'redux';
import {
    assignSubmissionToJuryurl,
    juryAssignmentSubmissionsUrl,
    juryAssignmentUrl,
    juryBaseUrl,
    acceptJuryInviteUrl,
    respondJuryInviteUrl,
    scoreSubmissionsUrl,
    scoreBoardUrl,
    submissionsUrl,
    juryUrl,
    emailJurorUrl,
    inviteExternalJurorUrl,
} from '../../configurations/api/url';
import {
    acceptJurySuccess,
    acceptJuryFail,
    respondJuryInviteSuccess,
    respondJuryInviteFail,
    distributeSubmissionToJuriesFail,
    distributeSubmissionToJuriesSuccess,
    getCampaignSubmissionsFail,
    getCampaignSubmissionsSuccess,
    getJuryCampaigns,
    getJuryCampaignsFail,
    getJuryCampaignsSuccess,
    inviteJuryFail,
    inviteJurySuccess,
    scoreSubmissionFail,
    scoreSubmissionSuccess,
    getJurorForContestSuccess,
    getJurorForContestFail,
    getScoreBoardSuccess,
    getScoreBoardFail,
    getCompletedCampaignSubmissionsSuccess,
    getIncompleteCampaignSubmissionsSuccess,
    searchCampaignSubmissionSuccess,
    deleteJurySuccess,
    deleteJuryFail,
    getSubmissionForAssignmentSuccess,
    getSubmissionForAssignmentFail,
    updateJurySuccess,
    updateJuryFail,
    emailJurorSuccess,
    emailJurorFail,
    inviteExternalJurorSuccess,
    inviteExternalJurorFail,
} from './action';
import {
    IAcceptJuryInvite,
    IRespondJuryInvite,
    IAssignJuryDutyAdvanced,
    IGetJurorForContest,
    IGetJuryCampaigns,
    IGetJuryCampaignSubmissions,
    IGetScoreBoard,
    IGetSubmissionsForAssignment,
    IInviteJuryPayload,
    IJuryDuties,
    IJurySetings,
    IEmailJurorPayload,
    IInviteExternalJurorPayload,
    IScoreSubmission,
    ISubmissionResultsForContest,
    ISubmissionsAssignmentResponse,
    types,
} from './types';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { genericParseSingleDocument, parseGenericCollection } from '../../utils/responseProcessor';
import { destroyOneCache, setCache } from '../cache/action';
import { CACHE_TYPE, CacheValue, NotUniqueCacheValue } from '../cache/types';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { showToastMessage } from '../../utils/AppUtils';
import { SentryCapture } from '../../analytics/Sentry';
import { IGenericDocumentModifier, IGetAllResponse } from '../../types/global/helper';
import { globalErrorHandler } from '../error/saga';
import { RootState } from '../root-reducer';
import { juryDutyContestIsDeleted } from '../contest/deletedContestIds';
import { navigate } from '../navigator/action';
import { ROUTES } from '../../types/global/routes.types';
import { updateNotification } from '../notifications/action';
import {
    JURY_DUTY_API_STATUSES,
    mergeJuryDutiesById,
    resolveJuryCampaignsForStore,
} from '../../components/EsCarousels/juryDutyObsidianShared';

const JURY_ASSIGNMENTS_PAGE_LIMIT = 50;
let juryCampaignsLoadAllInFlight = false;

function* filterDeletedJuryDuties(duties: IJuryDuties[]): Generator<any, IJuryDuties[], any> {
    const deletedContestIds: string[] | undefined = yield select((state: RootState) => state.contest.deletedContestIds);
    return duties.filter(duty => !juryDutyContestIsDeleted(duty, deletedContestIds));
}

function* fetchJuryCampaignPage(
    page: number,
    limit: number,
    status?: IGetJuryCampaigns['status'],
): Generator<any, { parsed: IJuryDuties[]; response: any }, any> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (status) params.append('status', String(status));
    const responseUrl = `${juryAssignmentUrl}?${params}`;
    const response: any = yield call(api, responseUrl, httpRequest.GET, null, 2, 1000);
    const { data }: { data: IJuryDuties[] } = response;
    const parsed = parseGenericCollection(data, genericParseSingleDocument, genericParseSingleDocument, 'contest');
    return { parsed, response };
}

function* loadAllJuryDutyAssignments(pageLimit: number): Generator<any, IJuryDuties[], any> {
    let merged: IJuryDuties[] = [];

    for (const status of JURY_DUTY_API_STATUSES) {
        let page = 1;
        let totalPages = 1;

        do {
            const { parsed, response } = yield* fetchJuryCampaignPage(page, pageLimit, status);
            merged = mergeJuryDutiesById([...merged, ...parsed]);
            totalPages = response.totalPages ?? 1;
            page += 1;
        } while (page <= totalPages);
    }

    return merged;
}

function* getJuryCampaign({
    payload,
    resolve,
    reject,
}: {
    payload: IGetJuryCampaigns;
    resolve: (data: IGetAllResponse<IJuryDuties>) => void;
    reject: (error: any) => void;
}): any {
    if (payload.loadAllPages && juryCampaignsLoadAllInFlight) {
        return;
    }

    const defaultUseCache = yield* isUseCacheEnabled();
    const statusCacheKey = payload.loadAllPages ? 'all-pages' : payload.status || 'all';
    let initialResult: any = null;
    const cache: CacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.JURY_CAMPAIGNS, statusCacheKey);
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (!!initialResult && defaultUseCache && !payload.skipCache) {
        const filteredCached: IJuryDuties[] = yield* filterDeletedJuryDuties(initialResult);
        yield put(getJuryCampaignsSuccess(filteredCached));
        if (resolve) {
            resolve(
                //@ts-ignore
                {
                    currentPage: initialResult.currentPage,
                    data: { data: initialResult.data },
                    pageSize: initialResult.pageSize,
                    status: initialResult.status,
                    totalCount: initialResult.totalCount,
                    totalPages: initialResult.totalPages,
                },
            );
        }
    } else {
        if (payload.loadAllPages && juryCampaignsLoadAllInFlight) {
            return;
        }
        if (payload.loadAllPages) {
            juryCampaignsLoadAllInFlight = true;
        }
        try {
            const pageLimit = payload.limit ?? JURY_ASSIGNMENTS_PAGE_LIMIT;
            const existingCampaigns: IJuryDuties[] = yield select((state: RootState) => state.jury.campaigns);

            if (payload.loadAllPages) {
                const merged: IJuryDuties[] = yield* loadAllJuryDutyAssignments(pageLimit);
                const filteredDuties: IJuryDuties[] = yield* filterDeletedJuryDuties(merged);
                const nextCampaigns = resolveJuryCampaignsForStore(existingCampaigns, filteredDuties);
                const totalCount = nextCampaigns.length;
                yield put(getJuryCampaignsSuccess(nextCampaigns));
                yield put(
                    setCache({
                        key: statusCacheKey,
                        value: nextCampaigns,
                        type: CACHE_TYPE.JURY_CAMPAIGNS,
                        isUnique: false,
                    }),
                );
                if (resolve) {
                    resolve(
                        //@ts-ignore
                        {
                            currentPage: 1,
                            data: { data: nextCampaigns },
                            pageSize: nextCampaigns.length,
                            status: 'success',
                            totalCount,
                            totalPages: 1,
                        },
                    );
                }
            } else {
                const params = new URLSearchParams();
                if (payload.page) params.append('page', String(payload.page));
                params.append('limit', String(pageLimit));
                if (payload.status) params.append('status', String(payload.status));
                const responseUrl = `${juryAssignmentUrl}?${params}`;
                const response: any = yield call(api, responseUrl, httpRequest.GET, null, 2, 1000);
                const { data }: { data: IJuryDuties[] } = response;
                const parsedJuryDuties = parseGenericCollection(
                    data,
                    genericParseSingleDocument,
                    genericParseSingleDocument,
                    'contest',
                );

                const filteredDuties: IJuryDuties[] = yield* filterDeletedJuryDuties(parsedJuryDuties);
                yield put(getJuryCampaignsSuccess(filteredDuties));
                yield put(
                    setCache({
                        key: statusCacheKey,
                        value: filteredDuties,
                        type: CACHE_TYPE.JURY_CAMPAIGNS,
                        isUnique: false,
                    }),
                );
                if (resolve) {
                    resolve(
                        //@ts-ignore
                        {
                            currentPage: response.currentPage,
                            data: { data: response.data },
                            pageSize: response.pageSize,
                            status: response.status,
                            totalCount: response.totalCount,
                            totalPages: response.totalPages,
                        },
                    );
                }
            }
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getJuryCampaignsFail(error));
            if (reject) {
                reject(error);
            }
        } finally {
            if (payload.loadAllPages) {
                juryCampaignsLoadAllInFlight = false;
            }
        }
    }
}

function* getSubmissionsForAssignment({
    payload,
    resolve,
    reject,
}: {
    payload: IGetSubmissionsForAssignment;
    resolve: (data: ISubmissionsAssignmentResponse) => Promise<ISubmissionsAssignmentResponse>;
    reject: (reason: any) => Promise<any>;
}): Generator<any, any, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const { contestId, searchTerm, category, limit, page, sortBy, sortOrder } = payload;

    const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
    });

    if (category) queryParams.append('category', category);
    if (searchTerm) queryParams.append('searchTerm', searchTerm);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);

    const param = `${contestId}/all?${queryParams.toString()}`;
    const request = `${submissionsUrl}/${param}`;

    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SUBMISSIONS_FOR_ASSIGNMENT, param);

    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (initialResult && defaultUseCache) {
        yield put(getSubmissionForAssignmentSuccess(initialResult));
        if (resolve) resolve(initialResult);
    } else {
        try {
            const response: ISubmissionsAssignmentResponse = yield call(api, request, httpRequest.GET, null, 0, 1000);

            yield put(getSubmissionForAssignmentSuccess(response));

            yield put(
                setCache({
                    key: param,
                    value: response,
                    type: CACHE_TYPE.SUBMISSIONS_FOR_ASSIGNMENT,
                    isUnique: false,
                }),
            );
            if (resolve) resolve(response);
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getSubmissionForAssignmentFail(error));
            if (reject) reject(error);
        }
    }
}

function* getJuryCampaignSubmission({ payload }: { payload: IGetJuryCampaignSubmissions }): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const { contestId, match, searchTerm, category } = payload;
    let param = `${contestId}?page=${payload.page}&limit=${payload.limit}`; // Pagination here
    if (match) {
        param += `&match=${match}`;
    }

    if (category) {
        param += `&category=${category}`;
    }

    if (searchTerm) {
        param += `&searchTerm=${searchTerm}`;
    }
    const request = `${juryAssignmentSubmissionsUrl}${param}`;
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.PROJECT, param);
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache) {
        if (match === 'all') {
            yield put(
                getCampaignSubmissionsSuccess({ ...initialResult, page: payload.page, limit: payload.limit, match }),
            );
        }

        if (match === 'judged') {
            yield put(
                getCompletedCampaignSubmissionsSuccess({
                    ...initialResult,
                    page: payload.page,
                    limit: payload.limit,
                    match,
                }),
            );
        }

        if (match === 'not_judged') {
            yield put(
                getIncompleteCampaignSubmissionsSuccess({
                    ...initialResult,
                    page: payload.page,
                    limit: payload.limit,
                    match,
                }),
            );
        }
    } else {
        try {
            const response = yield call(api, request, httpRequest.GET, null, 2, 1000);
            const { data }: { data: ISubmissionResultsForContest } = response;
            const { submissions, total_count } = data;
            const successBase = {
                submissions,
                total_count,
                page: payload.page,
                limit: payload.limit,
                match,
                paginatedReplace: payload.paginatedReplace,
            };

            if (match === 'all' || !match) {
                yield put(getCampaignSubmissionsSuccess(successBase));
            }

            if (match === 'judged') {
                yield put(getCompletedCampaignSubmissionsSuccess(successBase));
            }

            if (match === 'not_judged') {
                yield put(getIncompleteCampaignSubmissionsSuccess(successBase));
            }
            yield put(
                setCache({
                    key: param,
                    value: { submissions, total_count },
                    type: CACHE_TYPE.CAMPAIGN_SUBMISSIONS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getCampaignSubmissionsFail(error));
        }
    }
}

function* searchCampaignSubmission({ payload }: { payload: IGetJuryCampaignSubmissions }): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const { contestId, match, searchTerm, category } = payload;
    let param = `${contestId}?page=${payload.page}&limit=${payload.limit}`; // Pagination here
    if (match) {
        param += `&match=${match}`;
    }

    if (searchTerm) {
        param += `&searchTerm=${searchTerm}`;
    }

    if (category) {
        param += `&category=${category}`;
    }
    const request = `${juryAssignmentSubmissionsUrl}${param}`;
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.PROJECT, param);
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache) {
        if (match === 'all') {
            yield put(
                searchCampaignSubmissionSuccess({ ...initialResult, page: payload.page, limit: payload.limit, match }),
            );
        }
    } else {
        try {
            const response = yield call(api, request, httpRequest.GET, null, 2, 1000);
            const { data }: { data: ISubmissionResultsForContest } = response;
            const { submissions, total_count } = data;
            yield put(
                searchCampaignSubmissionSuccess({
                    submissions,
                    total_count,
                    page: payload.page,
                    limit: payload.limit,
                    match,
                }),
            );

            yield put(
                setCache({
                    key: param,
                    value: { submissions, total_count },
                    type: CACHE_TYPE.CAMPAIGN_SUBMISSIONS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getCampaignSubmissionsFail(error));
        }
    }
}

function* scoreSubmission({ payload }: { payload: IScoreSubmission }): any {
    try {
        const response = yield call(
            api,
            `${scoreSubmissionsUrl}${payload.submissionId}`,
            httpRequest.PATCH,
            payload.payload,
            2,
            1000,
        );
        const { data } = response;
        const parsedSubmission = data;
        yield put(scoreSubmissionSuccess({ submission: parsedSubmission, match: payload.match }));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.CAMPAIGN_SUBMISSIONS }));
        yield call(showToastMessage, 'Score Successful', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(scoreSubmissionFail(error));
        yield call(showToastMessage, error, 'error');
    }
}

function* updateJury({ payload }: { payload: IJurySetings }): Generator<any, void, any> {
    try {
        const response = yield call(api, `${juryUrl}/${payload.id}`, httpRequest.PATCH, { jury_settings: payload });
        const { data } = response.data;
        yield put(updateJurySuccess(data));
    } catch (error: any) {
        yield put(updateJuryFail(error));
    }
}

function* acceptJuryInvitation({ payload }: { payload: IAcceptJuryInvite }): any {
    try {
        yield call(api, `${acceptJuryInviteUrl}${payload.tokem}`, httpRequest.GET, null);
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.JURY_CAMPAIGNS,
            }),
        );
        yield put(
            acceptJurySuccess(
                'Congratulation, you are now officially a juror. Go to your jury duty tab to see assigned submissions',
            ),
        );
        yield put(getJuryCampaigns({ loadAllPages: true, skipCache: true }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            acceptJuryFail(
                'There seems to be an issue with this token. The token is either expired or has already been used',
            ),
        );
    }
}

function* respondJuryInviteSaga({ payload }: { payload: IRespondJuryInvite }): any {
    try {
        const response = yield call(
            api,
            respondJuryInviteUrl,
            httpRequest.POST,
            { contestId: payload.contestId, action: payload.action },
            2,
            1000,
        );
        const message =
            response?.data?.message || (payload.action === 'accept' ? 'Invite accepted' : 'Invite declined');
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.JURY_CAMPAIGNS,
            }),
        );
        if (payload.notificationId) {
            yield put(
                updateNotification({
                    documentId: payload.notificationId,
                    payload: { seen: true, hidden: true },
                }),
            );
        }
        yield put(respondJuryInviteSuccess({ action: payload.action, message }));
        yield call(showToastMessage, message, 'success');
        yield put(getJuryCampaigns({ loadAllPages: true, skipCache: true }));
        if (payload.action === 'accept') {
            yield put(navigate({ routes: ROUTES.ESJURY_DUTY, params: {} }));
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        const msg =
            error?.data?.message ||
            error?.message ||
            (payload.action === 'accept' ? 'Could not accept this jury invite' : 'Could not decline this jury invite');
        yield put(respondJuryInviteFail(msg));
        yield call(showToastMessage, msg, 'error');
    }
}

function* distributeSubmissions({ payload }: { payload: IAssignJuryDutyAdvanced }): any {
    try {
        const response = yield call(
            api,
            `${assignSubmissionToJuryurl}${payload.id}`,
            httpRequest.POST,
            payload,
            2,
            1000,
        );
        const { data, message } = response;
        yield put(distributeSubmissionToJuriesSuccess(data));
        yield call(showToastMessage, message, 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(distributeSubmissionToJuriesFail(error));
    }
}

function* inviteJury({ payload }: { payload: IInviteJuryPayload }): any {
    try {
        const response = yield call(api, `${juryBaseUrl}`, httpRequest.POST, payload, 2, 1000);
        const { data } = response;
        yield put(inviteJurySuccess(data));

        yield call(showToastMessage, data[0].message, 'success');
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.JURORS,
            }),
        );
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.JURY_CAMPAIGNS,
            }),
        );
        yield* getJurorForContest({
            payload: {
                contestId: payload.contest,
                isRefresh: true,
            },
        });
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(inviteJuryFail(error));
        yield call(showToastMessage, 'Invitation could not be sent', 'error');
    }
}

function* deleteJuryInvite({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        yield call(api, `${juryBaseUrl}/${payload.documentId}`, httpRequest.DELETE, payload, 2, 1000);

        yield call(showToastMessage, 'Jury invite deleted successfully', 'success');
        yield put(deleteJurySuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteJuryFail(error));
        yield call(showToastMessage, 'Invitation could not be deleted', 'error');
    }
}

function* getJurorForContest({ payload }: { payload: IGetJurorForContest }): Generator<any, any, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const { contestId, isRefresh } = payload;
    const pageLimit = payload.limit ?? 100;
    const page = payload.page ?? 1;
    const param = `${contestId}?page=${page}&limit=${pageLimit}`;
    const request = `${juryAssignmentUrl}/${contestId}?page=${page}&limit=${pageLimit}`;
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.JURORS, param);
    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (initialResult && defaultUseCache && !isRefresh) {
        yield put(getJurorForContestSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, request, httpRequest.GET, {}, 2);
            const { data } = response;
            const parsed = parseGenericCollection(data, genericParseSingleDocument, genericParseSingleDocument, 'jury');
            yield put(getJurorForContestSuccess(parsed));
            yield put(
                setCache({
                    key: param,
                    value: parsed,
                    type: CACHE_TYPE.JURORS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            yield put(getJurorForContestFail(error));
        }
    }
}

function* getScoreBoard({ payload }: { payload: IGetScoreBoard }): Generator<any, any, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const { contestId, isRefresh, category } = payload;
    let param = contestId;
    if (category) {
        param += `?category=${category}`;
    }
    const request = `${scoreBoardUrl}/${param}`;
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SCORE_BOARD, param);
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache && !isRefresh) {
        yield put(getScoreBoardSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, request, httpRequest.GET, {}, 2);
            const { data } = response;
            yield put(getScoreBoardSuccess(data));
            yield put(
                setCache({
                    key: param,
                    value: data,
                    type: CACHE_TYPE.SCORE_BOARD,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            yield put(getScoreBoardFail(error));
        }
    }
}

function* emailJuror({ payload }: { payload: IEmailJurorPayload }): Generator<any, any, any> {
    try {
        const response = yield call(
            api,
            emailJurorUrl,
            httpRequest.POST,
            { user: payload.userId, contest: payload.contestId, inviterName: payload.inviterName },
            2,
        );
        const { data } = response;
        yield put(emailJurorSuccess(data));
        yield call(showToastMessage, 'Email sent to juror successfully', 'success');
    } catch (error: any) {
        yield put(emailJurorFail(error));
        yield* globalErrorHandler(error);
    }
}

function* inviteExternalJuror({ payload }: { payload: IInviteExternalJurorPayload }): Generator<any, any, any> {
    try {
        const response = yield call(api, inviteExternalJurorUrl, httpRequest.POST, payload, 2);
        const { data } = response;
        yield put(inviteExternalJurorSuccess(data));
        yield call(showToastMessage, 'External jurors invited successfully', 'success');
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.JURORS,
            }),
        );
        yield* getJurorForContest({
            payload: {
                contestId: payload.contest,
                isRefresh: true,
            },
        });
    } catch (error: any) {
        yield put(inviteExternalJurorFail(error));
        yield* globalErrorHandler(error);
    }
}

interface TaskAction
    extends Action,
        IInviteJuryPayload,
        IAcceptJuryInvite,
        IScoreSubmission,
        IGetJuryCampaignSubmissions {
    payload: any;
    resolve?: any;
    reject?: any;
}

function* getJuryCampaignWatcher() {
    yield takeLatest<any>(types.GET_JURY_CAMPAIGNS, getJuryCampaign);
}

function* getJuryCampaignSubmissionWatcher() {
    yield takeLatest<TaskAction>(types.GET_CAMPAIGN_SUBMISSIONS, getJuryCampaignSubmission);
}

function* getIncompleteJuryCampaignSubmissionWatcher() {
    yield takeLatest<TaskAction>(types.GET_INCOMPLETE_CAMPAIGN_SUBMISSIONS, getJuryCampaignSubmission);
}

function* getCompleteJuryCampaignSubmissionWatcher() {
    yield takeLatest<TaskAction>(types.GET_COMPLETED_CAMPAIGN_SUBMISSIONS, getJuryCampaignSubmission);
}

function* scoreSubmissionWatcher(): any {
    yield takeLatest<TaskAction>(types.SCORE_SUBMISSION, scoreSubmission);
}

function* acceptJuryInvitationWatcher() {
    yield takeLatest<TaskAction>(types.ACCEPT_JURY_INVITE, acceptJuryInvitation);
}

function* respondJuryInviteWatcher() {
    yield takeLatest<TaskAction>(types.RESPOND_JURY_INVITE, respondJuryInviteSaga);
}

function* distributeSubmissionsWatcher() {
    yield takeLatest<TaskAction>(types.DISTRIBUTE_SUBMISSIONS_TO_JURIES, distributeSubmissions);
}

function* inviteJuryWatcher() {
    yield takeLatest<TaskAction>(types.INVITE_JURY, inviteJury);
}

function* getScoreBoardWatcher() {
    yield takeLatest<TaskAction>(types.GET_SCORE_BOARD, getScoreBoard);
}

function* getJurorForContestWatcher() {
    yield takeLatest<TaskAction>(types.GET_JURORS_FOR_CONTEST, getJurorForContest);
}

function* searchSumissionCamapignsWatcher() {
    yield takeLatest<TaskAction>(types.SEARCH_SUBMISSIONS, searchCampaignSubmission);
}

function* deleteJuyInviteWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_JURY, deleteJuryInvite);
}

function* getSubmissionsForAssignmentWatcher() {
    yield takeLatest<any>(types.GET_SUBMISSIONS_FOR_ASSIGNMENT, getSubmissionsForAssignment);
}

function* updateJuryWatcher() {
    yield takeLatest<any>(types.UPDATE_JURY, updateJury);
}

function* emailJurorWatcher() {
    yield takeLatest<any>(types.EMAIL_JUROR, emailJuror);
}

function* inviteExternalJurorWatcher() {
    yield takeLatest<any>(types.INVITE_EXTERNAL_JUROR, inviteExternalJuror);
}

export default function* jurySaga() {
    yield all([
        getJuryCampaignWatcher(),
        getJuryCampaignSubmissionWatcher(),
        scoreSubmissionWatcher(),
        acceptJuryInvitationWatcher(),
        respondJuryInviteWatcher(),
        distributeSubmissionsWatcher(),
        inviteJuryWatcher(),
        getScoreBoardWatcher(),
        getJurorForContestWatcher(),
        getIncompleteJuryCampaignSubmissionWatcher(),
        getCompleteJuryCampaignSubmissionWatcher(),
        searchSumissionCamapignsWatcher(),
        deleteJuyInviteWatcher(),
        getSubmissionsForAssignmentWatcher(),
        updateJuryWatcher(),
        emailJurorWatcher(),
        inviteExternalJurorWatcher(),
    ]);
}
